import React, { useMemo, useState } from "react";
import styled from "styled-components";
import StarBadge from "../../Components/SubscriptionsComponents/StarBadge";
import Tooltip from "../../Components/Generic/Tooltip";
import { UNSUBSCRIBE } from "../../constants/OptionsConstants";
import { ReactComponent as Check } from "../../assets/icons/CheckSvg.svg";
import { Info } from "lucide-react";
import { PRODUCTS, CARD_COLOR, renderProductIcon } from "../../constants/productConstants";
import { REACTIVATE_SUBSCRIPTION } from "../../constants/OptionsConstants";
import DeleteConfirmationChild from "../../Components/Generic/DeleteConfirmationWithChild";
const SubscriptionDetails = ({
  subscription,
  onRemoveProduct,
  onReactivate,
  confirmedUsers,
  benefits = [],
  compact = false,
}) => {
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [reactivateModalOpen, setReactivateModalOpen] = useState(false);
  

  const {
    productName,
    rawPlanName,
    billingCycle,
    formattedPrice,
    formattedNextRenewal,
    renewalText,
    isPendingCancel,
  } = useMemo(() => {
    if (!subscription) {
      return {
        productName: "Unknown Plan",
        rawPlanName: "",
        billingCycle: "Monthly",
        formattedPrice: "0.00",
        formattedNextRenewal: "-",
        renewalText: "Next renewal on",
        isPendingCancel: false,
      };
    }

    const planName = subscription.plan_name || "";
    const cycle = subscription.interval === "year" ? "Yearly" : "Monthly";

    const amount = (subscription.amount_due ?? 0) / 100;
    const currency = subscription.currency;

    const priceFormatter = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const pendingCancel = subscription.cancels_at != null;
    let formattedDate = "-";
    let renewalLabel = "Next renewal on";

    if (pendingCancel) {
      formattedDate = new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(new Date(subscription.cancels_at));
      renewalLabel = "Active until";
    } else if (subscription.next_payment_date) {
      formattedDate = new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(new Date(subscription.next_payment_date));
    }

    const productName = PRODUCTS[subscription.product_slug]?.title || subscription.product_name || "";

    return {
      productName: productName || planName,
      rawPlanName: productName ? planName : "",
      billingCycle: cycle,
      formattedPrice: priceFormatter.format(amount),
      formattedNextRenewal: formattedDate,
      renewalText: renewalLabel,
      isPendingCancel: pendingCancel,
    };
  }, [subscription]);

  const hasBenefits = benefits.length > 0;

  const tooltipContent = hasBenefits ? (
    <TooltipFeatureList>
      {benefits.map((benefit) => (
        <TooltipFeatureItem key={benefit}>
          <TooltipCheckIcon>
            <Check />
          </TooltipCheckIcon>
          <span>{benefit}</span>
        </TooltipFeatureItem>
      ))}
    </TooltipFeatureList>
  ) : null;

  if (compact) {
    const CompactIcon = renderProductIcon(subscription?.product_slug);
    return (
      <Card compact>
        <MainRow>
          <PlanTitle>
            <CompactIconWrap>{CompactIcon}</CompactIconWrap>
            <PlanTitleText compact>
              {productName}
              {rawPlanName && <PlanNameEm> {rawPlanName}</PlanNameEm>}
            </PlanTitleText>
          </PlanTitle>
          <Price compact>{formattedPrice}</Price>
        </MainRow>
        <MetaRow>
          <MetaLeft>
            <CyclePill pending={isPendingCancel}>
              {!isPendingCancel && <CycleDot />}
              {isPendingCancel ? "CANCELING" : billingCycle.toUpperCase()}
            </CyclePill>
            <MutedText compact>
              {renewalText} {formattedNextRenewal}
            </MutedText>
          </MetaLeft>
          {confirmedUsers != null && (
            <SeatsPill>{confirmedUsers} confirmed seats</SeatsPill>
          )}
        </MetaRow>
      </Card>
    );
  }

  const iconColor = isPendingCancel ? "#f97316" : CARD_COLOR;
  const ProductIcon = renderProductIcon(subscription?.product_slug, iconColor);
  const currencySymbol = formattedPrice[0];
  const [pricePart, decimalPart] = formattedPrice.slice(1).split(".");

  return (
    <Card pending={isPendingCancel}>
      <TopBar pending={isPendingCancel} />
      <CardInner>
        <HeaderRow>
          <IconWrap pending={isPendingCancel}>{ProductIcon}</IconWrap>
          <TitleRow>
            <Tooltip content={productName} side="bottom">
              <PlanTitleText>{productName}</PlanTitleText>
            </Tooltip>
            {hasBenefits && (
              <Tooltip content={tooltipContent} side="bottom">
                <InfoIconWrapper><Info size={14} /></InfoIconWrapper>
              </Tooltip>
            )}
          </TitleRow>
          <PillsRow>
            {confirmedUsers != null && (
              <SeatsPill>{confirmedUsers} confirmed seats</SeatsPill>
            )}
            <CyclePill pending={isPendingCancel}>
              <CycleDot pending={isPendingCancel} />
              {isPendingCancel ? "CANCELING" : billingCycle.toUpperCase()}
            </CyclePill>
          </PillsRow>
        </HeaderRow>

        <PriceRenewalRow>
          <FullPriceRow>
            <PriceCurrency>{currencySymbol}</PriceCurrency>
            <PriceWhole>{pricePart}.{decimalPart}</PriceWhole>
            <PriceInterval>/{billingCycle === "Yearly" ? "year" : "month"}</PriceInterval>
          </FullPriceRow>

          <RenewalBox pending={isPendingCancel}>
            <RenewalLabel pending={isPendingCancel}>{renewalText}</RenewalLabel>
            <RenewalDate>{formattedNextRenewal}</RenewalDate>
          </RenewalBox>
        </PriceRenewalRow>


        {isPendingCancel && onReactivate && (
          <DeleteConfirmationChild
            open={reactivateModalOpen}
            setOpen={setReactivateModalOpen}
            action={onReactivate}
            variant={REACTIVATE_SUBSCRIPTION}
          >
            <ReactivateButton onClick={() => setReactivateModalOpen(true)}>
              Reactivate subscription
            </ReactivateButton>
          </DeleteConfirmationChild>
        )}

        
        {!isPendingCancel && onRemoveProduct && (
          <DeleteConfirmationChild
            open={cancelModalOpen}
            setOpen={setCancelModalOpen}
            action={onRemoveProduct}
            variant={UNSUBSCRIBE}
          >
            <CancelButton onClick={() => setCancelModalOpen(true)}>
              Cancel subscription
            </CancelButton>
          </DeleteConfirmationChild>
        )}
      </CardInner>
    </Card>
  );
};

export default SubscriptionDetails;

const CyclePill = styled.span`
  background: ${(p) => (p.pending ? "#fff7ed" : "#ecfdf3")};
  color: ${(p) => (p.pending ? "#c4320a" : "#067647")};
  padding: 2px 8px;
  height: 22px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  border: 1px solid ${(p) => (p.pending ? "#fed7aa" : "#d1fadf")};
  gap: 5px;
`;

const CycleDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${(p) => (p.pending ? "#f97316" : "#12b76a")};
  flex-shrink: 0;
`;

const SeatsPill = styled.span`
  background: #b678f4;
  color: #ffffff;
  border: 1px solid #b678f4;
  padding: 2px 8px;
  height: 22px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
`;

const PlanTitleText = styled.span`
  color: #181d27;
  font-size: ${(p) => (p.compact ? "15px" : "17px")};
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
  min-width: 0;
`;

const PlanNameEm = styled.span`
  font-style: italic;
`;

const InfoIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  color: #9ca3af;
  cursor: pointer;
  transition: color 0.15s;

  &:hover { color: #7f56d9; }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.compact ? "10px" : "0")};
  width: 100%;
  background: #ffffff;
  border: ${(p) =>
    p.compact
      ? "1px solid #e9eaeb"
      : p.pending
      ? "1px solid #fed7aa"
      : "none"};
  border-radius: 16px;
  box-shadow:
    0 1px 2px rgba(10, 13, 18, 0.04),
    0 4px 12px rgba(10, 13, 18, 0.06);
  padding: ${(p) => (p.compact ? "16px" : "0")};
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.2s ease;

  ${(p) =>
    p.compact &&
    `
    &::before {
      content: "";
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 3px;
      background: ${
        p.pending
          ? "linear-gradient(90deg, #fb923c 0%, #f97316 50%, #ea580c 100%)"
          : "linear-gradient(90deg, #9b8afb 0%, #b678f4 50%, #d4a8fa 100%)"
      };
      border-radius: 16px 16px 0 0;
    }
  `}

  &:hover {
    box-shadow:
      0 2px 4px rgba(10, 13, 18, 0.05),
      0 8px 24px ${(p) =>
        p.pending
          ? "rgba(249, 115, 22, 0.12)"
          : "rgba(127, 86, 217, 0.12)"};
  }

  @media (max-width: 768px) {
    padding: ${(p) => (p.compact ? "16px" : "0")};
  }
`;

const CompactIconWrap = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f4ebff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  svg {
    width: 14px;
    height: 14px;
  }
`;

const MainRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const PlanTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Price = styled.div`
  color: #181d27;
  font-size: ${(p) => (p.compact ? "20px" : "26px")};
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1;
  white-space: nowrap;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding-top: 10px;
  border-top: 1px solid #f2f4f7;
`;

const MetaLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const MutedText = styled.span`
  color: #667085;
  font-size: ${(p) => (p.compact ? "12px" : "13px")};
  font-weight: 400;
`;

const TopBar = styled.div`
  height: 5px;
  background: ${(p) =>
    p.pending
      ? "linear-gradient(90deg, #fb923c 0%, #f97316 50%, #ea580c 100%)"
      : "linear-gradient(90deg, #9b8afb 0%, #b678f4 50%, #d4a8fa 100%)"};
  flex-shrink: 0;
`;

const CardInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px 22px 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PillsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const IconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${(p) => (p.pending ? "#fff7ed" : "#f4ebff")};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 22px;
    height: 22px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const PriceRenewalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const FullPriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  line-height: 1;
`;

const PriceCurrency = styled.span`
  color: #101828;
  font-size: 20px;
  font-weight: 700;
`;

const PriceWhole = styled.span`
  color: #101828;
  font-size: 36px;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1;
`;

const PriceInterval = styled.span`
  color: #667085;
  font-size: 13px;
  font-weight: 400;
`;

const RenewalBox = styled.div`
  background: ${(p) => (p.pending ? "#fff7ed" : "#f9fafb")};
  border: 1px solid ${(p) => (p.pending ? "#fed7aa" : "#f2f4f7")};
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
`;

const RenewalLabel = styled.span`
  color: ${(p) => (p.pending ? "#c4320a" : "#667085")};
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const RenewalDate = styled.span`
  color: #101828;
  font-size: 14px;
  font-weight: 700;
`;

const CancelButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1.5px solid #fda29b;
  color: #b42318;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #fff1f0;
    border-color: #f97066;
  }
`;

const ReactivateButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1.5px solid #f97316;
  color: #f97316;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #fff7ed;
    border-color: #ea580c;
    color: #ea580c;
  }
`;

const TooltipFeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
  max-width: 280px;
`;

const TooltipFeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #f9fafb;
  line-height: 18px;
`;

const TooltipCheckIcon = styled.span`
  width: 16px;
  height: 16px;
  min-width: 16px;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.15);
  margin-top: 1px;

  svg {
    width: 10px;
    height: 10px;
    stroke: white;
  }
`;